import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, Subject, map } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateAdapter } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpiritualAssistanceVisit, SpiritualAssistanceVisitApiService, SpiritualAssistanceVisitPerson } from '@core/api/services/spiritual-assistance-visit-api.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { DataTableMultiSelectFilterComponent, DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { DateRangePickerComponent } from '@shared/components/date-range-picker/date-range-picker.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { OrganizationUnitApiService, OrganizationUnitDto, OrganizationUnitTreeNodeDto } from '@core/api/services/organization-unit-api.service';
import { SpiritualAssistanceVisitsMoreFiltersDialogComponent, SpiritualAssistanceVisitsMoreFilters } from './spiritual-assistance-visits-more-filters-dialog.component';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { PersonMultiSelectFilterComponent, PersonFilterOption, PersonFilterSelection } from '@shared/components/person-multi-select-filter/person-multi-select-filter.component';
import { SpiritualAssistanceVisitResponsiblePriestFilterComponent } from './spiritual-assistance-visit-responsible-priest-filter.component';
import { OrganizationLevel } from '../units/models/unit.model';
import { BreadcrumbService } from '@core/services/breadcrumb.service';

@Component({
  selector: 'app-spiritual-assistance-visits-list',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatTooltipModule, DateRangePickerComponent, DataTableMultiSelectFilterComponent, PersonMultiSelectFilterComponent, SpiritualAssistanceVisitResponsiblePriestFilterComponent, TableSurfaceComponent],
  templateUrl: './spiritual-assistance-visits-list.component.html',
  styleUrl: './spiritual-assistance-visits.component.scss',
})
export class SpiritualAssistanceVisitsListComponent implements OnInit, OnDestroy {
  @ViewChild(TableSurfaceComponent) tableSurfaceRef?: TableSurfaceComponent<SpiritualAssistanceVisit>;

  unitId = '';
  unitName = '';
  visits: SpiritualAssistanceVisit[] = [];
  filteredVisits: SpiritualAssistanceVisit[] = [];
  loading = true;
  errorMessage = '';
  searchTerm = '';
  showCommunityColumn = false;
  activeFilterChips: FilterChip[] = [];
  sortState: ToolbarSortSelection = { sortBy: 'visitDate', direction: 'desc' };
  recipientOptions: DataTableMultiSelectFilterOption[] = [];
  visitorOptions: DataTableMultiSelectFilterOption[] = [];
  responsiblePriestOptions: DataTableMultiSelectFilterOption[] = [];
  locationOptions: DataTableMultiSelectFilterOption[] = [];
  communityOptions: DataTableMultiSelectFilterOption[] = [];
  districtOptions: DataTableMultiSelectFilterOption[] = [];
  areaOptions: DataTableMultiSelectFilterOption[] = [];
  readonly communionOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'with', label: 'Sí' },
    { value: 'without', label: 'No' },
  ];

  private recipientsInitialized = false;
  private locationsInitialized = false;
  private communitiesInitialized = false;
  private districtsInitialized = false;
  private areasInitialized = false;
  private canReadMultipleUnits = false;
  private unitOrganizationLevelId: number | null = null;
  private readonly visitHierarchy = new Map<string, VisitHierarchy>();
  private readonly destroy$ = new Subject<void>();

  readonly filterForm = new FormGroup({
    dateRange: new FormGroup({ start: new FormControl<any | null>(null), end: new FormControl<any | null>(null) }),
    recipient: new FormControl<string[]>([], { nonNullable: true }),
    visitor: new FormControl<PersonFilterSelection>(null),
    responsiblePriest: new FormControl<string[]>([], { nonNullable: true }),
    location: new FormControl<string[]>([], { nonNullable: true }),
    communion: new FormControl<string[]>(this.communionOptions.map(option => option.value), { nonNullable: true }),
    community: new FormControl<string[]>([], { nonNullable: true }),
    district: new FormControl<string[]>([], { nonNullable: true }),
    area: new FormControl<string[]>([], { nonNullable: true }),
  });

  readonly allColumns: TableColumn[] = [
    { key: 'visitDate', header: 'Fecha', sortable: true, hideable: true, width: '9%' },
    { key: 'recipients', header: 'Miembros', sortable: true, hideable: true, width: '16%' },
    { key: 'responsiblePriests', header: 'Pastor de hogar', sortable: true, hideable: true, width: '16%' },
    { key: 'community', header: 'Comunidad', sortable: true, hideable: true, width: '11%' },
    { key: 'visitors', header: 'Asistentes espirituales', sortable: true, hideable: true, width: '20%' },
    { key: 'location', header: 'Lugar', sortable: true, hideable: true, width: '8%' },
    { key: 'communion', header: 'Santa Cena', sortable: true, hideable: true, width: '10%' },
    { key: 'actions', header: '', sortable: false, width: '10%', exportable: false },
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: SpiritualAssistanceVisitApiService,
    private readonly dialog: MatDialog,
    public readonly user: UserFullInfoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly organizationUnitApi: OrganizationUnitApiService,
    private readonly breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (params, route) => ({ ...params, ...route.params }), {} as Record<string, string>
    );
    this.unitId = allParams['unit-id'] ?? '';
    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyTableState());
    this.user.getUserInfo$().pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.canReadMultipleUnits = (user?.readableUnitIds?.length ?? 0) > 1;
      this.updateCommunityColumnVisibility();
      this.updateScopeFilterOptions();
      this.applyTableState();
      this.cdr.markForCheck();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get columns(): TableColumn[] {
    return this.allColumns.filter(column =>
      (this.showCommunityColumn || column.key !== 'community') &&
      (this.user.hasPermission('ManageSpiritualAssistanceVisits') || column.key !== 'actions'));
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api.list(this.unitId).subscribe({
      next: visits => {
        this.visits = visits;
        this.updateFilterOptions();
        this.applyTableState();
        this.loadScopeHierarchy();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'No se pudieron cargar las visitas de asistencia espiritual.';
        this.cdr.markForCheck();
      },
    });
  }

  readonly searchVisitors = (term: string, page: number) => this.api.listAvailableVisitors(term, page).pipe(
    map(response => ({ ...response, items: response.items.map(person => ({
      id: person.id,
      fullName: person.fullName,
      description: [
        person.ministryName ? person.ministryName + (person.ministryActivityUnitNames.length ? ' en ' + person.ministryActivityUnitNames.join(', ') : '') : '',
        person.officiantActivityUnitNames.length ? 'Oficiante en ' + person.officiantActivityUnitNames.join(', ') : '',
      ].filter(Boolean).join(' / '),
    })) })),
  );

  create(): void { this.router.navigate(['new'], { relativeTo: this.route }); }
  edit(visit: SpiritualAssistanceVisit): void { this.router.navigate([visit.id, 'edit'], { relativeTo: this.route }); }

  onVisitorSelectionChange(visitors: PersonFilterOption[]): void {
    this.visitorOptions = visitors.map(visitor => ({ value: visitor.id, label: visitor.fullName }));
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  delete(visit: SpiritualAssistanceVisit): void {
    const data: DeleteConfirmDialogData = {
      title: 'Eliminar VAE',
      message: `¿Querés eliminar la VAE del ${this.formatDate(visit.visitDate)}? Esta acción no se puede deshacer.`,
      confirmButtonText: 'Eliminar',
    };
    this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({ data, size: 'sm', layout: 'confirm', tone: 'danger' }))
      .afterClosed().subscribe(confirmed => {
        if (confirmed) this.api.delete(visit.id).subscribe({ next: () => this.load(), error: () => this.cdr.markForCheck() });
      });
  }

  onToolbarSearch(term: string): void { this.searchTerm = term.trim(); this.applyTableState(); }
  onToolbarSort(sort: ToolbarSortSelection): void { this.sortState = sort; this.applyTableState(); }

  get hasAdvancedFilters(): boolean {
    const filters = this.filterForm.getRawValue();
    return filters.location.length !== this.locationOptions.length ||
      filters.communion.length !== this.communionOptions.length ||
      (this.showCommunityColumn && (
        filters.community.length !== this.communityOptions.length ||
        filters.district.length !== this.districtOptions.length ||
        filters.area.length !== this.areaOptions.length));
  }

  openMoreFilters(): void {
    const filters = this.filterForm.getRawValue();
    this.dialog.open(SpiritualAssistanceVisitsMoreFiltersDialogComponent, modalDialogConfig({
      size: 'md',
      layout: 'form',
      data: {
        locationOptions: this.locationOptions,
        communionOptions: this.communionOptions,
        communityOptions: this.communityOptions,
        districtOptions: this.districtOptions,
        areaOptions: this.areaOptions,
        showCommunityFilters: this.showCommunityColumn,
        values: {
          location: filters.location,
          communion: filters.communion,
          community: filters.community,
          district: filters.district,
          area: filters.area,
        },
      },
    })).afterClosed().subscribe((result: SpiritualAssistanceVisitsMoreFilters | null) => {
      if (result) this.filterForm.patchValue(result);
    });
  }

  onFilterRemove(key: string): void {
    if (key === 'search') {
      this.searchTerm = '';
      this.tableSurfaceRef?.clearSearch();
      this.applyTableState();
      return;
    }
    if (key === 'dateRange') {
      this.filterForm.controls.dateRange.reset({ start: null, end: null });
      return;
    }
    const options: Record<string, DataTableMultiSelectFilterOption[]> = {
      recipient: this.recipientOptions,
      visitor: this.visitorOptions,
      responsiblePriest: this.responsiblePriestOptions,
      location: this.locationOptions,
      communion: this.communionOptions,
      community: this.communityOptions,
      district: this.districtOptions,
      area: this.areaOptions,
    };
    if (key === 'visitor') {
      this.filterForm.controls.visitor.setValue(null);
      return;
    }
    if (key === 'responsiblePriest') {
      this.filterForm.controls.responsiblePriest.setValue([]);
      return;
    }
    (this.filterForm.get(key) as FormControl<string[]> | null)?.setValue((options[key] ?? []).map(option => option.value));
  }

  formatDate(value: string): string {
    const date = new Date(`${value}T00:00:00`);
    const weekday = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][date.getDay()];
    return `${weekday} ${new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)}`;
  }

  formatPeople(people: SpiritualAssistanceVisitPerson[]): string { return people.map(person => person.fullName).join(', ') || '-'; }

  getResponsiblePriests(visit: SpiritualAssistanceVisit): SpiritualAssistanceVisitPerson[] {
    return [...new Map(visit.recipients
      .filter(person => !!person.responsiblePriestId && !!person.responsiblePriestName)
      .map(person => [person.responsiblePriestId!, { id: person.responsiblePriestId!, fullName: person.responsiblePriestName! }]))
      .values()];
  }

  formatResponsiblePriests(visit: SpiritualAssistanceVisit): string { return this.formatPeople(this.getResponsiblePriests(visit)); }

  getLocation(visit: SpiritualAssistanceVisit): string {
    if (visit.locationType === 'Home') return 'Hogar';
    if (visit.locationType === 'Community') return 'Comunidad';
    return visit.locationDescription || 'Otro lugar';
  }

  private applyTableState(): void {
    const filters = this.filterForm.getRawValue();
    const start = filters.dateRange?.start ? this.toIsoDate(filters.dateRange.start) : null;
    const end = filters.dateRange?.end ? this.toIsoDate(filters.dateRange.end) : null;
    const search = this.normalizeText(this.searchTerm);
    this.filteredVisits = this.sortVisits(this.visits.filter(visit => {
      if (start && visit.visitDate < start) return false;
      if (end && visit.visitDate > end) return false;
      if (!filters.recipient.length || !visit.recipients.some(person => filters.recipient.includes(person.id))) return false;
      if (filters.visitor !== null && !visit.visitors.some(person => filters.visitor!.includes(person.id))) return false;
      if (filters.responsiblePriest.length && !this.getResponsiblePriests(visit).some(person => filters.responsiblePriest.includes(person.id))) return false;
      if (!filters.location.length || !filters.location.includes(this.getLocation(visit))) return false;
      if (!filters.communion.length || !filters.communion.includes(visit.holyCommunionDelivered ? 'with' : 'without')) return false;
      const hierarchy = this.visitHierarchy.get(visit.organizationUnitId);
      if (this.showCommunityColumn && this.communitiesInitialized && (!filters.community.length || !filters.community.includes(visit.organizationUnitId))) return false;
      if (this.showCommunityColumn && this.districtsInitialized && (!filters.district.length || !hierarchy?.districtId || !filters.district.includes(hierarchy.districtId))) return false;
      if (this.showCommunityColumn && this.areasInitialized && (!filters.area.length || !hierarchy?.areaId || !filters.area.includes(hierarchy.areaId))) return false;
      return !search || this.getSearchText(visit).includes(search);
    }));
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  private updateFilterOptions(): void {
    this.recipientOptions = this.peopleToOptions(this.visits.flatMap(visit => visit.recipients));
    this.responsiblePriestOptions = this.peopleToOptions(this.visits.flatMap(visit => this.getResponsiblePriests(visit)));
    this.locationOptions = this.uniqueSorted(this.visits.map(visit => this.getLocation(visit))).map(value => ({ value }));
    if (!this.recipientsInitialized) { this.recipientsInitialized = true; this.filterForm.controls.recipient.setValue(this.recipientOptions.map(option => option.value), { emitEvent: false }); }
    if (!this.locationsInitialized) { this.locationsInitialized = true; this.filterForm.controls.location.setValue(this.locationOptions.map(option => option.value), { emitEvent: false }); }
  }

  private loadScopeHierarchy(): void {
    forkJoin({
      tree: this.organizationUnitApi.getDescendantTree(this.unitId),
      root: this.organizationUnitApi.getById(this.unitId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ tree, root }) => {
          this.setVisitHierarchy(tree, root);
          this.updateScopeFilterOptions();
          this.applyTableState();
        },
      });
  }

  private setVisitHierarchy(tree: OrganizationUnitTreeNodeDto | null | undefined, root: OrganizationUnitDto): void {
    this.unitName = root.name ?? '';
    this.breadcrumbService.setDynamicCrumb('unit-id', this.unitName);
    this.unitOrganizationLevelId = root.organizationLevelId ?? null;
    this.updateCommunityColumnVisibility();
    const units = new Map<string, OrganizationUnitTreeNodeDto>();
    const collect = (unit: OrganizationUnitTreeNodeDto | null | undefined): void => {
      if (!unit?.id) return;
      units.set(unit.id, unit);
      unit.children?.forEach(collect);
    };
    collect(tree);

    this.visitHierarchy.clear();
    this.visits.forEach(visit => {
      let current = units.get(visit.organizationUnitId);
      const hierarchy: VisitHierarchy = {};
      while (current) {
        if (current.organizationLevelId === 3) {
          hierarchy.districtId = current.id;
          hierarchy.districtName = current.name;
        } else if (current.organizationLevelId === 2) {
          hierarchy.areaId = current.id;
          hierarchy.areaName = current.name;
        }
        current = current.parentUnitId ? units.get(current.parentUnitId) : undefined;
      }
      hierarchy.districtId ??= root.organizationLevelId === 3 ? root.id : root.districtId ?? undefined;
      hierarchy.districtName ??= root.organizationLevelId === 3 ? root.name : root.districtName ?? undefined;
      hierarchy.areaId ??= root.organizationLevelId === 2 ? root.id : root.areaId ?? undefined;
      hierarchy.areaName ??= root.organizationLevelId === 2 ? root.name : root.areaName ?? undefined;
      this.visitHierarchy.set(visit.organizationUnitId, hierarchy);
    });
  }

  private updateCommunityColumnVisibility(): void {
    this.showCommunityColumn = this.canReadMultipleUnits && this.unitOrganizationLevelId !== null && this.unitOrganizationLevelId !== OrganizationLevel.Comunidad;
  }

  private updateScopeFilterOptions(): void {
    if (!this.showCommunityColumn || this.visits.length === 0) return;

    this.communityOptions = this.uniqueOptions(this.visits.map(visit => ({ value: visit.organizationUnitId, label: visit.organizationUnitName })));
    this.districtOptions = this.uniqueOptions(this.visits
      .map(visit => this.visitHierarchy.get(visit.organizationUnitId))
      .filter((hierarchy): hierarchy is VisitHierarchy & Required<Pick<VisitHierarchy, 'districtId' | 'districtName'>> => !!hierarchy?.districtId && !!hierarchy.districtName)
      .map(hierarchy => ({ value: hierarchy.districtId, label: hierarchy.districtName })));
    this.areaOptions = this.uniqueOptions(this.visits
      .map(visit => this.visitHierarchy.get(visit.organizationUnitId))
      .filter((hierarchy): hierarchy is VisitHierarchy & Required<Pick<VisitHierarchy, 'areaId' | 'areaName'>> => !!hierarchy?.areaId && !!hierarchy.areaName)
      .map(hierarchy => ({ value: hierarchy.areaId, label: hierarchy.areaName })));

    if (!this.communitiesInitialized) { this.communitiesInitialized = true; this.filterForm.controls.community.setValue(this.communityOptions.map(option => option.value), { emitEvent: false }); }
    if (!this.districtsInitialized && this.visitHierarchy.size > 0) { this.districtsInitialized = true; this.filterForm.controls.district.setValue(this.districtOptions.map(option => option.value), { emitEvent: false }); }
    if (!this.areasInitialized && this.visitHierarchy.size > 0) { this.areasInitialized = true; this.filterForm.controls.area.setValue(this.areaOptions.map(option => option.value), { emitEvent: false }); }
  }

  private buildFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];
    if (this.searchTerm) chips.push({ key: 'search', label: `Búsqueda: ${this.searchTerm}`, removable: true });
    const start = filters.dateRange?.start ? this.toIsoDate(filters.dateRange.start) : null;
    const end = filters.dateRange?.end ? this.toIsoDate(filters.dateRange.end) : null;
    if (start || end) chips.push({ key: 'dateRange', label: `Fecha: ${start || '...'} - ${end || '...'}`, removable: true });
    this.addMultiSelectChip(chips, 'recipient', 'Miembro', filters.recipient, this.recipientOptions);
    if (filters.visitor !== null) {
      const selection = filters.visitor.map(value => this.visitorOptions.find(option => option.value === value)?.label ?? value).join(', ');
      chips.push({ key: 'visitor', label: `Asistente espiritual: ${selection || 'Ninguno'}`, removable: true });
    }
    if (filters.responsiblePriest.length) {
      const selection = filters.responsiblePriest.map(value => this.responsiblePriestOptions.find(option => option.value === value)?.label ?? value).join(', ');
      chips.push({ key: 'responsiblePriest', label: `Pastor de hogar: ${selection}`, removable: true });
    }
    this.addMultiSelectChip(chips, 'location', 'Lugar', filters.location, this.locationOptions);
    this.addMultiSelectChip(chips, 'communion', 'Santa Cena', filters.communion, this.communionOptions);
    if (this.showCommunityColumn) {
      this.addMultiSelectChip(chips, 'community', 'Comunidad', filters.community, this.communityOptions);
      this.addMultiSelectChip(chips, 'district', 'Distrito', filters.district, this.districtOptions);
      this.addMultiSelectChip(chips, 'area', 'Área Apóstol', filters.area, this.areaOptions);
    }
    this.activeFilterChips = chips;
  }

  private addMultiSelectChip(chips: FilterChip[], key: string, label: string, values: string[], options: DataTableMultiSelectFilterOption[]): void {
    if (values.length === options.length) return;
    const selection = values.length === 0 ? 'Ninguno' : values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');
    chips.push({ key, label: `${label}: ${selection}`, removable: true });
  }

  private sortVisits(visits: SpiritualAssistanceVisit[]): SpiritualAssistanceVisit[] {
    return [...visits].sort((left, right) => {
      const result = this.getSortValue(left, this.sortState.sortBy).localeCompare(this.getSortValue(right, this.sortState.sortBy), 'es', { sensitivity: 'base' });
      return this.sortState.direction === 'asc' ? result : -result;
    });
  }

  private getSortValue(visit: SpiritualAssistanceVisit, column: string): string {
    switch (column) {
      case 'recipients': return this.formatPeople(visit.recipients);
      case 'visitors': return this.formatPeople(visit.visitors);
      case 'responsiblePriests': return this.formatResponsiblePriests(visit);
      case 'location': return this.getLocation(visit);
      case 'community': return visit.organizationUnitName;
      case 'communion': return visit.holyCommunionDelivered ? 'Sí' : 'No';
      default: return visit.visitDate;
    }
  }

  private getSearchText(visit: SpiritualAssistanceVisit): string {
    return this.normalizeText([visit.visitDate, this.formatDate(visit.visitDate), this.formatPeople(visit.recipients), this.formatResponsiblePriests(visit), visit.organizationUnitName, this.formatPeople(visit.visitors), this.getLocation(visit), visit.holyCommunionDelivered ? 'Sí' : 'No'].join(' '));
  }

  private peopleToOptions(people: SpiritualAssistanceVisitPerson[]): DataTableMultiSelectFilterOption[] {
    const uniquePeople = new Map(people.map(person => [person.id, person]));
    return [...uniquePeople.values()].sort((a, b) => a.fullName.localeCompare(b.fullName, 'es', { sensitivity: 'base' })).map(person => ({ value: person.id, label: person.fullName }));
  }

  private uniqueOptions(options: DataTableMultiSelectFilterOption[]): DataTableMultiSelectFilterOption[] {
    return [...new Map(options.map(option => [option.value, option])).values()]
      .sort((left, right) => (left.label ?? '').localeCompare(right.label ?? '', 'es', { sensitivity: 'base' }));
  }

  private uniqueSorted(values: string[]): string[] { return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })); }
  private toIsoDate(value: any): string { return `${this.dateAdapter.getYear(value)}-${String(this.dateAdapter.getMonth(value) + 1).padStart(2, '0')}-${String(this.dateAdapter.getDate(value)).padStart(2, '0')}`; }
  private normalizeText(value: string): string { return value.toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }
}

interface VisitHierarchy {
  districtId?: string;
  districtName?: string;
  areaId?: string;
  areaName?: string;
}
